import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  Check,
  ExternalLink,
  GitCommitHorizontal,
  Loader2,
  Rocket,
  ThumbsDown,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '@/hooks/use-api';
import { Header } from '@/components/layout/header';
import { LoadingState } from '@/components/loading-state';
import { ErrorState } from '@/components/error-state';
import { MetricCard } from '@/components/metric-card';
import {
  prewarmNote,
  promoteBlockedReason,
  releaseCycle,
  runStatusBadge,
  shortSha,
  type PrewarmStatus,
  type StepState,
} from '@/lib/release-status';
import { cn } from '@/lib/utils';
import { postJson } from '@/lib/post-json';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface WorkflowRunView {
  status: string;
  conclusion: string | null;
  htmlUrl: string;
  createdAt: string;
}

interface ReleaseStatusResponse {
  staging: {
    manifest: {
      phase: string;
      built_sha: string;
      updated_at: string;
      frontend_url: string;
      backend_url: string;
      prewarm?: PrewarmStatus | null;
    } | null;
    error: string | null;
  };
  prod: { sha: string | null; error: string | null };
  pending: {
    aheadBy: number | null;
    commits: {
      sha: string;
      message: string;
      author: string;
      date: string;
      pr: { number: number; url: string } | null;
      tickets: { id: string; url: string }[];
    }[];
    error: string | null;
    ticketsError: string | null;
  };
  runs: {
    promote: WorkflowRunView | null;
    deployStaging: WorkflowRunView | null;
    error: string | null;
  };
}

type ReleaseAction = 'deploy-staging' | 'teardown-staging' | 'promote';

const REPO_URL = 'https://github.com/Overhead-Analytics/Overhead-Analytics';

function StepMarker({ n, state }: { n: number; state: StepState }) {
  if (state === 'done') {
    return (
      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[var(--status-success-bg)] text-[var(--status-success-fg)]">
        <Check className="size-4" />
      </span>
    );
  }
  if (state === 'busy') {
    return (
      <span className="border-border flex size-7 shrink-0 items-center justify-center rounded-full border">
        <Loader2 className="text-muted-foreground size-4 animate-spin" />
      </span>
    );
  }
  if (state === 'failed') {
    return (
      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[var(--status-danger-bg)] text-[var(--status-danger-fg)]">
        <AlertCircle className="size-4" />
      </span>
    );
  }
  return (
    <span
      className={cn(
        'flex size-7 shrink-0 items-center justify-center rounded-full border text-sm font-medium tabular-nums',
        state === 'active'
          ? 'border-foreground text-foreground'
          : 'border-border text-muted-foreground'
      )}
    >
      {n}
    </span>
  );
}

function TicketLinks({
  tickets,
  pr,
}: {
  tickets: { id: string; url: string }[];
  pr: { number: number; url: string } | null;
}) {
  if (tickets.length === 0) {
    // Link the gap to where it gets fixed: the PR description is the source.
    return pr ? (
      <a
        href={pr.url}
        target="_blank"
        rel="noreferrer"
        className="text-muted-foreground text-xs underline-offset-4 hover:underline"
      >
        no ticket linked in #{pr.number}
      </a>
    ) : (
      <span className="text-muted-foreground text-xs">no ticket linked</span>
    );
  }
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1">
      {tickets.map((t) => (
        <a
          key={t.id}
          href={t.url}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-1 font-mono text-xs underline-offset-4 hover:underline"
        >
          <ExternalLink className="size-3" />
          {/* Notion page ids share a long workspace prefix; the tail tells them apart. */}
          {t.id.slice(-8)}
        </a>
      ))}
    </div>
  );
}

function RunLink({ run, label }: { run: WorkflowRunView | null; label: string }) {
  if (!run) return null;
  const badge = runStatusBadge(run);
  return (
    <a
      href={run.htmlUrl}
      target="_blank"
      rel="noreferrer"
      className={cn(
        'inline-flex items-center gap-1 text-xs underline-offset-4 hover:underline',
        badge.status === 'critical' ? 'text-[var(--status-danger-fg)]' : 'text-muted-foreground'
      )}
    >
      {label}: {badge.label} <ExternalLink className="size-3" />
    </a>
  );
}

export function ReleasePage() {
  const { data, error, loading, refresh, lastFetched } =
    useApi<ReleaseStatusResponse>('/api/release/status');
  const [actionLoading, setActionLoading] = useState<ReleaseAction | null>(null);
  const [confirmAction, setConfirmAction] = useState<
    'promote' | 'deploy' | 'teardown' | 'reject' | null
  >(null);

  const act = useCallback(
    async (action: ReleaseAction, body?: object) => {
      setActionLoading(action);
      try {
        const { runsUrl } = (await postJson(`/api/release/${action}`, body)) as {
          runsUrl: string;
        };
        toast.success(
          action === 'promote'
            ? 'Promotion started — deploys run automatically after validation'
            : action === 'deploy-staging'
              ? 'Staging deploy started (~10–15 min; ~20 if rebuilding)'
              : 'Staging teardown started',
          {
            action: { label: 'View run', onClick: () => window.open(runsUrl, '_blank') },
            duration: 8000,
          }
        );
        refresh();
      } catch (err) {
        toast.error(`${action} failed: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setActionLoading(null);
        setConfirmAction(null);
      }
    },
    [refresh]
  );

  // Auto-refresh while a deploy or promotion is in flight (the server keeps a
  // just-dispatched action visible as a queued run, so this covers the moment
  // after a click too). Matches the server cache TTL — polling faster than the
  // cache refreshes buys nothing.
  const inFlight = Boolean(
    data &&
    ((data.runs.deployStaging && data.runs.deployStaging.status !== 'completed') ||
      (data.runs.promote && data.runs.promote.status !== 'completed') ||
      (data.staging.manifest &&
        data.staging.manifest.phase !== 'HEALTHY' &&
        data.staging.manifest.phase !== 'FAILED'))
  );
  useEffect(() => {
    if (!inFlight) return;
    const id = setInterval(refresh, 15_000);
    return () => clearInterval(id);
  }, [inFlight, refresh]);

  if (!data) {
    return (
      <>
        <Header title="Release" lastFetched={lastFetched} onRefresh={refresh} loading={loading} />
        <div className="space-y-6 p-6">
          {error ? <ErrorState message={error} onRetry={refresh} /> : <LoadingState rows={3} />}
        </div>
      </>
    );
  }

  const cycle = releaseCycle(
    data.staging,
    data.prod.sha,
    data.runs.promote,
    data.runs.deployStaging
  );
  const blocked = promoteBlockedReason(data.staging, data.prod.sha);
  const warmNote = prewarmNote(data.staging.manifest);
  const builtSha = data.staging.manifest?.built_sha ?? null;
  const stagingUp = data.staging.manifest !== null;
  const pendingCount = data.pending.aheadBy;

  const confirmDialogs: Record<
    'promote' | 'deploy' | 'teardown' | 'reject',
    {
      title: string;
      description: React.ReactNode;
      confirmLabel: string;
      icon?: React.ReactNode;
      variant: 'default' | 'destructive';
      action: () => void;
    }
  > = {
    promote: {
      title: 'Promote to production',
      description: (
        <>
          Ships build <span className="font-mono">{shortSha(builtSha)}</span> — the exact code
          staging is running — including {pendingCount === null ? 'the' : pendingCount} pending
          change
          {pendingCount === 1 ? '' : 's'}. Staging is torn down automatically after a successful
          promotion.
        </>
      ),
      confirmLabel: `Promote ${shortSha(builtSha)}`,
      icon: <Rocket className="size-4" />,
      variant: 'default',
      action: () => builtSha && act('promote', { confirmSha: builtSha }),
    },
    deploy: {
      title: 'Rebuild staging',
      description: (
        <>
          Staging is currently up (build {shortSha(builtSha)}). Rebuilding tears it down — ending
          any test session in progress — and deploys a fresh environment from the latest changes
          (~20 min).
        </>
      ),
      confirmLabel: 'Rebuild',
      variant: 'destructive',
      action: () => act('deploy-staging'),
    },
    teardown: {
      title: 'Tear down staging',
      description: (
        <>
          Removes the staging environment (build {shortSha(builtSha)}) — ending any test session in
          progress. Redeploying later takes ~10–15 min.
        </>
      ),
      confirmLabel: 'Tear down',
      variant: 'destructive',
      action: () => act('teardown-staging'),
    },
    reject: {
      title: 'Reject this build',
      description: (
        <>
          Nothing ships: staging (build {shortSha(builtSha)}) is torn down and production stays as
          it is. The {pendingCount ?? 'pending'} change
          {pendingCount === 1 ? '' : 's'} stay queued — tell engineering what failed so they can fix
          or revert before the next cycle.
        </>
      ),
      confirmLabel: 'Reject build',
      icon: <ThumbsDown className="size-4" />,
      variant: 'destructive',
      action: () => act('teardown-staging'),
    },
  };
  const confirm = confirmAction ? confirmDialogs[confirmAction] : null;

  const steps: {
    title: string;
    description: string;
    cycleStep: { state: StepState; detail: string };
    action: React.ReactNode;
    footer?: React.ReactNode;
  }[] = [
    {
      title: 'Stage',
      description:
        'Build a fresh test environment from the latest changes — a full copy of production data with the pending work applied.',
      cycleStep: cycle.stage,
      action: (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={() => (stagingUp ? setConfirmAction('deploy') : act('deploy-staging'))}
            disabled={actionLoading !== null || cycle.stage.state === 'busy'}
            size="lg"
            className="min-w-44"
            variant={
              cycle.stage.state === 'active' || cycle.stage.state === 'failed'
                ? 'default'
                : 'outline'
            }
          >
            {actionLoading === 'deploy-staging' ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <UploadCloud className="size-4" />
            )}
            {stagingUp ? 'Rebuild staging' : 'Deploy staging'}
          </Button>
          {stagingUp && cycle.stage.state !== 'busy' && (
            <Button
              onClick={() => setConfirmAction('teardown')}
              disabled={actionLoading !== null}
              variant="outline"
              size="lg"
            >
              <Trash2 className="size-4" />
              Tear down
            </Button>
          )}
        </div>
      ),
      footer: <RunLink run={data.runs.deployStaging} label="last deploy" />,
    },
    {
      title: 'Test',
      description:
        'Check the pending changes on staging with your usual login. It stays up for 24 hours.',
      cycleStep: cycle.test,
      action: (
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              asChild={cycle.test.state === 'active'}
              disabled={cycle.test.state !== 'active'}
              size="lg"
              className="min-w-44"
              variant={cycle.test.state === 'active' ? 'default' : 'outline'}
            >
              {cycle.test.state === 'active' && data.staging.manifest ? (
                <a href={data.staging.manifest.frontend_url} target="_blank" rel="noreferrer">
                  Open staging <ExternalLink className="size-4" />
                </a>
              ) : (
                <span className="inline-flex items-center gap-2 whitespace-nowrap">
                  Open staging <ExternalLink className="size-4" />
                </span>
              )}
            </Button>
            {cycle.test.state === 'active' && (
              <Button
                onClick={() => setConfirmAction('reject')}
                disabled={actionLoading !== null}
                variant="outline"
                size="lg"
                className="text-destructive hover:text-destructive"
              >
                <ThumbsDown className="size-4" />
                Reject build
              </Button>
            )}
          </div>
          {cycle.test.state !== 'active' && (
            <span className="text-muted-foreground text-xs">{cycle.test.detail}</span>
          )}
          {cycle.test.state === 'active' && warmNote && (
            <span className="text-muted-foreground text-xs">{warmNote}</span>
          )}
        </div>
      ),
    },
    {
      title: 'Ship',
      description:
        'Promote exactly what staging is running. Production deploys start automatically and staging is cleaned up.',
      cycleStep: cycle.ship,
      action: (
        <div className="flex flex-col gap-1.5">
          <Button
            onClick={() => setConfirmAction('promote')}
            disabled={actionLoading !== null || cycle.ship.state !== 'active'}
            size="lg"
            className="min-w-44"
            variant={cycle.ship.state === 'active' ? 'default' : 'outline'}
          >
            {actionLoading === 'promote' ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Rocket className="size-4" />
            )}
            Promote to production
          </Button>
          {cycle.ship.state !== 'active' && (
            <span className="text-muted-foreground text-xs">{blocked ?? cycle.ship.detail}</span>
          )}
        </div>
      ),
      footer: <RunLink run={data.runs.promote} label="last promotion" />,
    },
  ];

  return (
    <>
      <Header title="Release" lastFetched={lastFetched} onRefresh={refresh} loading={loading} />
      <div className="space-y-6 p-6">
        <div className="grid gap-4 md:grid-cols-2">
          <MetricCard
            label="In production"
            value={shortSha(data.prod.sha)}
            subValue={data.prod.error ?? 'current release'}
          />
          <MetricCard
            label="Waiting to ship"
            value={pendingCount === null ? '—' : pendingCount}
            subValue={
              data.pending.error
                ? 'change list unavailable'
                : pendingCount === 0
                  ? 'production is up to date'
                  : 'merged changes, not yet in production'
            }
          />
        </div>

        <Card>
          <CardContent className="pt-6">
            <ol className="grid gap-8 md:grid-cols-3">
              {steps.map((step, i) => (
                <li key={step.title} className="flex flex-col gap-2.5">
                  <div className="flex items-center gap-3">
                    <StepMarker n={i + 1} state={step.cycleStep.state} />
                    <span className="font-medium">{step.title}</span>
                    <span className="text-muted-foreground truncate text-xs">
                      {step.cycleStep.detail}
                    </span>
                  </div>
                  <p className="text-muted-foreground text-sm text-pretty">{step.description}</p>
                  <div className="mt-auto pt-1">{step.action}</div>
                  {step.footer && <div>{step.footer}</div>}
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              Waiting to ship{' '}
              <a
                href={`${REPO_URL}/compare/prod...main`}
                target="_blank"
                rel="noreferrer"
                className="text-muted-foreground ml-2 text-sm font-normal underline-offset-4 hover:underline"
              >
                compare on GitHub
              </a>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.pending.error ? (
              <p className="text-muted-foreground text-sm">
                Pending list unavailable ({data.pending.error}) — the GitHub token may lack
                contents-read permission.
              </p>
            ) : data.pending.commits.length === 0 ? (
              <p className="text-muted-foreground text-sm">Production is up to date with main.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-28">Commit</TableHead>
                    <TableHead>Change</TableHead>
                    <TableHead className="w-56">Ticket</TableHead>
                    <TableHead className="w-40">Author</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.pending.commits.map((c) => (
                    <TableRow key={c.sha}>
                      <TableCell className="font-mono text-xs">
                        <a
                          href={`${REPO_URL}/commit/${c.sha}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1 underline-offset-4 hover:underline"
                        >
                          <GitCommitHorizontal className="size-3" />
                          {shortSha(c.sha)}
                        </a>
                      </TableCell>
                      <TableCell>{c.message}</TableCell>
                      <TableCell>
                        <TicketLinks tickets={c.tickets} pr={c.pr} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">{c.author}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {data.pending.ticketsError && (
              <p className="text-muted-foreground mt-3 text-xs">
                Ticket links unavailable ({data.pending.ticketsError}) — the GitHub App may lack
                pull-requests read permission.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={confirmAction !== null}
        onOpenChange={(open) => !open && setConfirmAction(null)}
      >
        <DialogContent>
          {confirm && (
            <>
              <DialogHeader>
                <DialogTitle>{confirm.title}</DialogTitle>
                <DialogDescription>{confirm.description}</DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirmAction(null)}>
                  Cancel
                </Button>
                <Button
                  variant={confirm.variant}
                  onClick={confirm.action}
                  disabled={actionLoading !== null}
                >
                  {confirm.icon}
                  {confirm.confirmLabel}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
